import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useAgeVerificationGate } from "@/hooks/useAgeVerificationGate";
import { useAuthGateRecovery } from "@/hooks/useAuthGateRecovery";
import { AGE_GATE_PATH, ageGateUrl } from "@/lib/ageGate";
import { AuthAccessStatus } from "./AuthAccessStatus";

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading: authLoading, initializationFailed } = useAuth();
  const { approvalStatus, isLoading: roleLoading } = useUserRole();
  const { ready: ageReady, promptRequired, lookupFailed } = useAgeVerificationGate();
  const location = useLocation();

  const isLoading = authLoading || roleLoading || (isAuthenticated && !ageReady);

  const failed = useAuthGateRecovery(isLoading, initializationFailed || (isAuthenticated && lookupFailed), authLoading ? "auth" : roleLoading ? "role" : "age");
  if (failed || isLoading) {
    return <AuthAccessStatus failed={failed} signInHref={authLoading ? `/auth?redirect=${encodeURIComponent(location.pathname + location.search)}` : undefined} />;
  }

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?redirect=${redirect}`} replace />;
  }

  if (approvalStatus === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }

  if (approvalStatus === 'rejected') {
    return <Navigate to="/login" replace />;
  }

  if (promptRequired && location.pathname !== AGE_GATE_PATH) {
    return <Navigate to={ageGateUrl(location.pathname + location.search)} replace />;
  }

  return <>{children}</>;
};
